import {
  Card,
  CardContent,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { formatValue } from "components/ui/formatters";
import { SingleValueCardColor, SingleValueCardType } from "constants/index";
import { useState, useCallback } from "react";
import { MultiValuesCardProps } from "helpers/types";

const MultiValuesCard = ({
  title,
  values,
  description,
  additionalComponents,
}: MultiValuesCardProps) => {
  // Track editing state for each field
  const [editingStates, setEditingStates] = useState<{
    [key: number]: { isEditing: boolean; editingValue: string };
  }>({});

  const handleFocus = useCallback(
    (index: number, currentValue: number | string) => {
      setEditingStates((prev) => ({
        ...prev,
        [index]: {
          isEditing: true,
          editingValue: currentValue.toString(),
        },
      }));
    },
    [],
  );

  const handleBlur = useCallback(
    (
      index: number,
      value: number | string,
      type: SingleValueCardType,
      onValueChange: (value: number | string) => void,
    ) => {
      const editingState = editingStates[index];
      if (!editingState) return;

      setEditingStates((prev) => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });

      let parsedValue: number | string;
      if (type === SingleValueCardType.CURRENCY) {
        parsedValue = parseFloat(
          editingState.editingValue.replace(/[$,\s]/g, ""),
        );
      } else if (type === SingleValueCardType.PERCENTAGE) {
        parsedValue = parseFloat(
          editingState.editingValue.replace(/[%+\s]/g, ""),
        );
      } else {
        parsedValue = parseFloat(
          editingState.editingValue.replace(/[,\s]/g, ""),
        );
      }

      if (!isNaN(Number(parsedValue))) {
        onValueChange(parsedValue);
      }
    },
    [editingStates],
  );

  const handleKeyDown = useCallback(
    (
      event: React.KeyboardEvent,
      index: number,
      value: number | string,
      type: SingleValueCardType,
      onValueChange: (value: number | string) => void,
    ) => {
      if (event.key === "Enter") {
        handleBlur(index, value, type, onValueChange);
      } else if (event.key === "Escape") {
        setEditingStates((prev) => {
          const newState = { ...prev };
          delete newState[index];
          return newState;
        });
      }
    },
    [handleBlur],
  );

  const getDisplayColor = (color?: SingleValueCardColor) => {
    return color || SingleValueCardColor.PRIMARY;
  };

  return (
    <Card
      sx={{
        display: "flex",
        justifyContent: "left",
        alignItems: "center",
        mt: 2,
      }}
    >
      <CardContent sx={{ width: "100%" }}>
        <Typography
          variant="h5"
          sx={{
            mb: 2,
            color: "#ffffff",
            backgroundColor: "#003057",
            padding: 1,
            borderRadius: 1,
          }}
        >
          {title}
        </Typography>
        <Grid container spacing={3}>
          {values.map((item, index) => {
            const editingState = editingStates[index];
            const isEditing = editingState?.isEditing || false;
            const numericValue =
              typeof item.value === "number"
                ? item.value
                : parseFloat(item.value.toString()) || 0;
            const displayColor = getDisplayColor(item.color);
            const formattedValue = formatValue(numericValue, item.type);

            const gridSize = item.fullWidthRow ? 12 : 6;
            return (
              <Grid item xs={12} md={gridSize} key={index}>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.5, color: "text.secondary" }}
                >
                  {item.label}:
                </Typography>
                <TextField
                  value={
                    isEditing && editingState
                      ? editingState.editingValue
                      : formattedValue
                  }
                  onFocus={() => handleFocus(index, item.value)}
                  onBlur={() =>
                    handleBlur(index, item.value, item.type, item.onValueChange)
                  }
                  onKeyDown={(e) =>
                    handleKeyDown(
                      e,
                      index,
                      item.value,
                      item.type,
                      item.onValueChange,
                    )
                  }
                  onChange={(e) => {
                    setEditingStates((prev) => ({
                      ...prev,
                      [index]: {
                        ...prev[index],
                        editingValue: e.target.value,
                      },
                    }));
                  }}
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      fontSize: "1.5rem",
                      fontWeight: 500,
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                      lineHeight: 1.2,
                      color: (theme) => theme.palette[displayColor].main,
                      "& input": {
                        textAlign: "left",
                        padding: 0,
                      },
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      fontSize: "1.5rem",
                    },
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
        {additionalComponents}
        {description != "" ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {description}
            </Typography>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default MultiValuesCard;
